import type { RouteRecordRaw, NavigationGuard } from 'vue-router';
import Permission from '@/composables/permissions/permissions';
import useNewOrgStore from '@/modules/create-org/store';
import { FeatureFlag } from '@/composables/feature-flags';
import { requireFlag } from './guards/flag';

function requireOrganisationStep(step: number): NavigationGuard {
  return (_to, _from, next) => {
    const newOrgStore = useNewOrgStore();
    if (newOrgStore.unlockedStep < step) {
      next({ name: 'suppliers-new' });
    } else {
      next();
    }
  };
}

export default [
  {
    path: 'suppliers/new',
    name: 'suppliers-new',
    component: () => import('@/modules/create-org/SupplierNew.vue'),
    meta: {
      permissions: [Permission.ClientsSuppliersManage],
    },
    redirect: { name: 'suppliers-new-organisation' },
    children: [
      {
        path: 'org',
        name: 'suppliers-new-organisation',
        component: () => import('@/modules/create-org/Organisation.vue'),
      },
      {
        path: 'contact',
        name: 'suppliers-new-contact',
        component: () => import('@/modules/create-org/Contact.vue'),
        beforeEnter: [requireOrganisationStep(2)],
      },
    ],
  },
  {
    // Legacy redirect - supplier profile and public profile have been merged
    path: 'suppliers/:id/public',
    name: 'suppliers-public-profile',
    redirect: { name: 'suppliers-profile' },
  },
  {
    path: 'suppliers/:id',
    name: 'suppliers-profile',
    component: () => import('@/modules/profile/SupplierProfile.vue'),
    props: true,
    redirect: { name: 'suppliers-profile-overview' },
    meta: {
      permissions: [Permission.ClientsSuppliersView],
    },
    children: [
      {
        name: 'suppliers-profile-overview',
        path: 'overview',
        component: () => import('@/modules/profile/components/overview/OverviewTab.vue'),
      },
      {
        name: 'suppliers-profile-activity',
        path: 'activity',
        component: () => import('@/modules/activity-feed/ActivityFeedConnection.vue'),
      },
      {
        name: 'suppliers-profile-assessment',
        path: 'assessment',
        component: () => import('@/modules/profile/components/assessment/AssessmentTab.vue'),
      },
      {
        name: 'suppliers-profile-external-monitoring',
        path: 'external-monitoring',
        beforeEnter: [requireFlag(FeatureFlag.ExternalMonitoringPaidClient)],
        component: () => import('@/modules/external-monitoring/SupplierProfileMonitoring.vue'),
        props: true,
        redirect: { name: 'suppliers-profile-external-monitoring/scans' },
        children: [
          {
            name: 'suppliers-profile-external-monitoring/scans',
            path: 'scans',
            component: () => import('@/modules/external-monitoring/SupplierProfileScans.vue'),
          },
          {
            name: 'suppliers-profile-external-monitoring/assets',
            path: 'assets',
            component: () => import('@/modules/external-monitoring/AssetsPagesCombined.vue'),
          },
          {
            name: 'suppliers-profile-external-monitoring/findings',
            path: 'findings',
            component: () => import('@/modules/external-monitoring/SupplierProfileFindings.vue'),
            props: true,
          },
        ],
      },
      {
        name: 'suppliers-profile-threats',
        path: 'threats/:threatId?',
        component: () => import('@/modules/emerging-threats-common/ThreatsProfileTab.vue'),
        meta: {
          permissions: [Permission.ClientsEmergingThreatsView],
        },
        props: true,
      },
      {
        name: 'suppliers-profile-risks',
        path: 'risks/:riskNumber?',
        component: () => import('@/modules/risks/RisksProfileTab.vue'),
        props: true,
      },
      {
        name: 'suppliers-profile-evidence',
        path: 'evidence',
        component: () => import('@/modules/profile/components/evidence/EvidenceOverview.vue'),
      },
      {
        name: 'suppliers-profile-discussions',
        path: 'discussions/:discussionID?',
        component: () => import('@/modules/discussions/DiscussionsTabView.vue'),
        props: (route) => ({
          discussionId: route.params.discussionID,
          query: route.query,
        }),
      },
      {
        name: 'suppliers-profile-notes',
        path: 'notes',
        component: () => import('@/modules/client-notes/ClientNotes.vue'),
        meta: {
          permissions: [Permission.ClientsSuppliersNotesView],
        },
      },
      {
        name: 'suppliers-profile-about',
        path: 'about',
        component: () => import('@/modules/org-info/OrgInfo.vue'),
        redirect: { name: 'suppliers-profile-about/basic-info' },
        children: [
          {
            name: 'suppliers-profile-about/basic-info',
            path: 'info',
            props: true,
            component: () => import('@/modules/org-info/OrgInfoBasic.vue'),
          },
          {
            name: 'suppliers-profile-about/products',
            path: 'products',
            props: true,
            component: () => import('@/modules/org-info/OrgInfoProducts.vue'),
          },
          {
            name: 'suppliers-profile-about/critical-suppliers',
            path: 'critical-suppliers',
            props: true,
            component: () => import('@/modules/org-info/OrgInfoCriticalSuppliers.vue'),
          },
        ],
      },
    ],
  },
  {
    path: 'policies',
    children: [
      {
        path: '',
        name: 'policies',
        component: () => import('@/modules/policy/PolicyListPage.vue'),
      },
      {
        path: 'matrix',
        name: 'policy-matrix',
        component: () => import('@/modules/policy/PolicyMatrix.vue'),
      },
      {
        path: ':id',
        name: 'policies-add-edit',
        meta: {
          permissions: [Permission.ClientsPoliciesManage],
        },
        component: () => import('@/modules/policy/components/policy-page/PolicyPage.vue'),
        props: true,
      },
    ],
  },
] satisfies RouteRecordRaw[];
